// src/pages/AccountPage.js
//
// Self-service "my account" — voluntary profile edit + change-password
// for an already-logged-in Employee. Didn't exist before: the only
// password-change page was PasswordChange.js, reachable exclusively
// from the forced first-login flow (no "current password" field, no
// voluntary entry point). Backed by gf/views_account.py's my_account /
// change_my_password — new, separate endpoints from pwreset (which has
// no current-password check and no ownership check, so isn't reused
// for a voluntary "change my own password anytime" surface).

import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import axiosInstance from '../services/axiosInstance';

export default function AccountPage() {
    const [account, setAccount] = useState(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [mobileNo, setMobileNo] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        axiosInstance
            .get('/api/my-account/')
            .then((res) => {
                setAccount(res.data);
                setFirstName(res.data.first_name || '');
                setLastName(res.data.last_name || '');
                setMobileNo(res.data.mobile_no || '');
            })
            .catch((err) => handleApiError(err))
            .finally(() => setLoading(false));
    }, []);

    const saveProfile = async () => {
        setSaving(true);
        try {
            const res = await axiosInstance.patch('/api/my-account/', {
                first_name: firstName,
                last_name: lastName,
                mobile_no: mobileNo,
            });
            setAccount(res.data);
            handleApiSuccess('Account details saved.');
        } catch (err) {
            handleApiError(err);
        } finally {
            setSaving(false);
        }
    };

    const changePassword = async () => {
        if (!currentPassword || !newPassword) {
            handleApiError({ Error: 'Current and new password are both required.' });
            return;
        }
        if (newPassword !== confirmPassword) {
            handleApiError({ Error: "New password and confirmation don't match." });
            return;
        }
        setChangingPassword(true);
        try {
            await axiosInstance.post('/api/my-account/change-password/', {
                current_password: currentPassword,
                new_password: newPassword,
            });
            handleApiSuccess('Password changed.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            handleApiError(err);
        } finally {
            setChangingPassword(false);
        }
    };

    if (loading || !account) return <p style={{ padding: 20 }}>Loading…</p>;

    return (
        <div style={{ maxWidth: 640, margin: '20px auto', padding: '0 16px' }}>
            <Card title="My Account" className="mb-4">
                <div className="formgrid grid">
                    <div className="field col">
                        <label className="font-bold">User ID</label>
                        <InputText value={String(account.id)} disabled style={{ width: '100%' }} />
                    </div>
                    <div className="field col">
                        <label className="font-bold">Username</label>
                        <InputText value={account.username} disabled style={{ width: '100%' }} />
                    </div>
                </div>
                <div className="field">
                    <label className="font-bold">Email</label>
                    <InputText value={account.email || '—'} disabled style={{ width: '100%' }} />
                </div>

                <Divider />

                <div className="formgrid grid">
                    <div className="field col">
                        <label className="font-bold">First name</label>
                        <InputText value={firstName} onChange={(e) => setFirstName(e.target.value)} style={{ width: '100%' }} />
                    </div>
                    <div className="field col">
                        <label className="font-bold">Last name</label>
                        <InputText value={lastName} onChange={(e) => setLastName(e.target.value)} style={{ width: '100%' }} />
                    </div>
                </div>
                <div className="field">
                    <label className="font-bold">Mobile number</label>
                    <InputText value={mobileNo} onChange={(e) => setMobileNo(e.target.value)} style={{ width: '100%' }} />
                </div>

                <Button label={saving ? 'Saving…' : 'Save'} icon="pi pi-check" disabled={saving} onClick={saveProfile} />
            </Card>

            <Card title="Change Password">
                <div className="field">
                    <label className="font-bold">Current password</label>
                    <Password
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        feedback={false}
                        toggleMask
                        style={{ width: '100%' }}
                        inputStyle={{ width: '100%' }}
                    />
                </div>
                <div className="field">
                    <label className="font-bold">New password</label>
                    <Password
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        toggleMask
                        style={{ width: '100%' }}
                        inputStyle={{ width: '100%' }}
                    />
                </div>
                <div className="field">
                    <label className="font-bold">Confirm new password</label>
                    <Password
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        feedback={false}
                        toggleMask
                        style={{ width: '100%' }}
                        inputStyle={{ width: '100%' }}
                    />
                </div>
                <Button
                    label={changingPassword ? 'Saving…' : 'Change password'}
                    icon="pi pi-lock"
                    disabled={changingPassword}
                    onClick={changePassword}
                />
            </Card>
        </div>
    );
}
